// Redux slice for the production board.
//
// The board's *data* is not in here — it is fetched by
// controller/use-production-board-data.ts and never leaves that hook, because
// nothing outside the board reads it and it is replaced wholesale on every
// import. What is in here is the state a user would be annoyed to lose when
// they switch tabs.
import { createSlice } from "@reduxjs/toolkit";

import { ProductionOrder } from "../types/production-board-types";
import {
	BoardFilters,
	BoardUnit,
	BoardView,
	PartsFilter,
	ProductionBoardRootState,
	ProductionBoardState,
	RowMode,
	ScheduleBasis,
	ScheduleMove,
} from "./production-board-types";

/** the forecast chart's opening span, in weeks — one of utils/board-filters' HORIZONS */
export const DEFAULT_HORIZON = 52;

const initialFilters: BoardFilters = {
	sitePc: "all",
	basis: "start",
	partsFilter: "any",
	rowMode: "customer",
	budgetTags: [],
	orderTypes: [],
	customers: [],
	horizon: DEFAULT_HORIZON,
	team: null,
	unit: "money",
};

const initialState: ProductionBoardState = {
	view: "exec",
	filters: initialFilters,
	moves: {},
	selectedWeek: null,
};

export interface MoveOrdersPayload {
	/** the orders being moved, with the week each one is leaving */
	orders: { jobOrderNumber: number; fromWeek: string | null }[]
	toWeek: string
	reason: string
	wasInsideLock: boolean
}

const productionBoardSlice = createSlice({
	name: "productionBoard",
	initialState,
	reducers: {
		setView(state, action: { payload: BoardView }) {
			state.view = action.payload;
		},
		setSite(state, action: { payload: number | "all" }) {
			state.filters.sitePc = action.payload;
		},
		/**
		 * Applies whatever the filter bar changed. It comes in as a partial
		 * because the bar reports one category at a time, and the tabs each
		 * render a different subset — a filter the current tab does not show
		 * must keep the value it had rather than being reset to its default.
		 */
		setBoardFilters(state, action: { payload: Partial<BoardFilters> }) {
			// Written key by key rather than as a whole new object: replacing
			// `state.filters` wholesale makes immer see a change even when every
			// value is the one it already held, and `selectBoardFilters` then
			// hands its subscribers a new reference on every dispatch.
			Object.entries(action.payload).forEach(([key, value]) => {
				const field = key as keyof BoardFilters;
				const current = state.filters[field];
				if (Array.isArray(value) && Array.isArray(current)) {
					if (
						value.length === current.length &&
						value.every((item, index) => item === current[index])
					) {
						return;
					}
				} else if (value === current) {
					return;
				}
				(state.filters as Record<string, unknown>)[field] = value;
			});
		},
		moveOrders(state, action: { payload: MoveOrdersPayload }) {
			const { orders, toWeek, reason, wasInsideLock } = action.payload;
			orders.forEach(({ jobOrderNumber, fromWeek }) => {
				state.moves[jobOrderNumber] = {
					field: "startWeek",
					fromWeek,
					toWeek,
					reason,
					wasInsideLock,
				};
			});
		},
		resetMoves(state) {
			state.moves = {};
		},
		setSelectedWeek(state, action: { payload: string | null }) {
			state.selectedWeek = action.payload;
		},
		setBasis(state, action: { payload: ScheduleBasis }) {
			state.filters.basis = action.payload;
		},
	},
});

// ---------- selectors ----------
// Templates and controllers read through these, so the state shape above stays
// this file's business.

export const selectView = (state: ProductionBoardRootState): BoardView =>
	state.productionBoard.view;

export const selectSitePc = (state: ProductionBoardRootState): number | "all" =>
	state.productionBoard.filters.sitePc;

export const selectBoardFilters = (
	state: ProductionBoardRootState
): BoardFilters => state.productionBoard.filters;

export const selectPartsFilter = (state: ProductionBoardRootState): PartsFilter =>
	state.productionBoard.filters.partsFilter;

export const selectRowMode = (state: ProductionBoardRootState): RowMode =>
	state.productionBoard.filters.rowMode;

export const selectBudgetTags = (state: ProductionBoardRootState): string[] =>
	state.productionBoard.filters.budgetTags;

export const selectOrderTypes = (state: ProductionBoardRootState): string[] =>
	state.productionBoard.filters.orderTypes;

export const selectCustomers = (state: ProductionBoardRootState): string[] =>
	state.productionBoard.filters.customers;

export const selectHorizon = (state: ProductionBoardRootState): number =>
	state.productionBoard.filters.horizon;

export const selectTeam = (state: ProductionBoardRootState): string | null =>
	state.productionBoard.filters.team;

export const selectUnit = (state: ProductionBoardRootState): BoardUnit =>
	state.productionBoard.filters.unit;

export const selectMoves = (
	state: ProductionBoardRootState
): Record<number, ScheduleMove> => state.productionBoard.moves;

export const selectSelectedWeek = (
	state: ProductionBoardRootState
): string | null => state.productionBoard.selectedWeek;

export const selectBasis = (state: ProductionBoardRootState): ScheduleBasis =>
	state.productionBoard.filters.basis;

/**
 * The order list the boards actually render: the fetched orders with this
 * session's moves laid over them.
 *
 * It takes the orders as an argument rather than reading them from state
 * because they are not in the store (see the file header). Memoizing it is
 * controller/use-schedule-moves.ts's job — it is the only caller.
 */
export function selectEffectiveOrders(
	orders: ProductionOrder[],
	moves: Record<number, ScheduleMove>
): ProductionOrder[] {
	if (Object.keys(moves).length === 0) return orders;
	return orders.map((order) => {
		const move = moves[order.jobOrderNumber];
		return move
			? { ...order, startWeek: move.toWeek, movedReason: move.reason }
			: order;
	});
}

export const {
	moveOrders,
	resetMoves,
	setBasis,
	setBoardFilters,
	setSelectedWeek,
	setSite,
	setView,
} = productionBoardSlice.actions;

export default productionBoardSlice.reducer;
